<template>
	<div>
		<div
			:style="$grid('5-1')"
			class="credit-application-lender-wrapper"
			ref="inputsend"
		>
			<InputRichDropdown
				label="Select a lender"
				lazyLoad
				:list="sortedLenderList"
				:valueMap="valuePredicate"
				:display="displayPredicate"
				:desc="descriptionPredicate"
				:iconCondition="balloonIconCondition"
				v-model:saturn="dropdownSelectedLender"
				:sortMode="DROPDOWN_SORT_MODE.ORIGINAL_ORDER"
				:invalid="!fimenu.dealType"
				:disabled="!fimenu.getCurrentBuyersOrder() || isEvaluatingExpresions || loadingCustomFields || onlyShowBalloonLenders || fimenu.isDealFinalized()"
				search
				class="ignore-deal-lock"
				@change="handleLenderChange"
			>
				<template #item-icon>
					<img
						src="@static/fa/svgs/custom/BalloonPayments.svg"
						alt="balloon-payments"
					/>
				</template>
				<template #validation>
					<span>You must select a deal type before selecting a lender.</span>
				</template>
			</InputRichDropdown>

			<button
				:disabled="!dropdownSelectedLender || balloonAmountIsNeeded || isEvaluatingExpresions || loadingCustomFields"
				class="button-block ignore-deal-lock"
				@click="handleSendButtonAction"
			>
				<span v-if="isEvaluatingExpresions || loadingCustomFields">
					<i class="fas fa-spinner fa-spin" />
				</span>
				<span v-else>Send</span>
			</button>
		</div>
		<div
			ref="applicationLenderPanel"
			style="paddingbottom: 10px"
		>
			<PanelDealTerms
				v-if="displayBOPanelDealTerms"
				:fimenu="props.fimenu"
				:panelObject="dealTermsPanelObj"
				:validation
				:setGapPricing="() => fimenu.priceProducts([{ productType: PRODUCT_TYPES.GAP }], null, false)"
				:dealSave="() => fimenu.save()"
				:hideTerms="true"
				:dealTermsChangedHandler="dealTermsChangedHandler"
			/>
		</div>

		<Panel
			v-bind="{ title: 'Applications' }"
			class="panel-routeOneDealerTrack"
		>
			<template #header>
				<PanelCreditApplicationErrorMessage
					:canSubmitCreditApplication
					:creditApplications
					:fimenuLenderCode="dealHasNoLender ? null : fimenu.lender?.lenderCode"
					:selectedLender="dealHasNoLender ? null : (dropdownSelectedLender as VersionsLender)"
					:preSelectedLenderCode="fimenu.preSelectedLenders"
				/>

				<div style="display: flex; justify-content: space-between; align-items: center; gap: 25px">
					<LenderStatusIcons
						:approvedApplications="approvedApplications"
						:conditionalApplications="conditionalApplications"
						:deniedApplications="deniedApplications"
					/>
				</div>
			</template>
			<IsBusyScreenComponent v-if="isLoading" />
			<div :style="$grid('1')">
				<div class="applications-box-container">
					<!-- LENDERS -->
					<div
						v-for="(lender, index) in preSelectedLendersWithNoApplications"
						:key="index"
						class="ApplicationsBox no-margin-grid"
					>
						<PCALenderContainer
							:lendersThatAllowAutoSubmit="lendersThatAllowAutoSubmit ?? []"
							:lenderName="lender.lenderName"
							:lenderCode="lender.lenderCode"
							:applications="[]"
							:onlyShowBalloonLenders="onlyShowBalloonLenders"
							:lendersList="lendersList"
							:reserveProfitCalcs="reserveProfitCalcs"
							:fimenu="fimenu"
							:send="handleSendButtonAction"
							:dealHasNoLender="dealHasNoLender"
							:showBalloonIcon="showBalloonIcon"
							:handleSelectLender="handleSelectLender"
							:balloonLenders="balloonLenders"
							:openSendModal="openSendModal"
							:fetchDynamicFields="fetchDynamicFields"
							:anyApplicationIsSelected="applicationIsSelected"
							:loadingCustomFields="loadingCustomFields"
							@ApplicationSelected="handleApplicationSelected"
						/>
					</div>
					<!-- CREDIT APPLICATIONS -->
					<div
						v-for="(applications, lenderCode, index) in getSortedApplicationsGroupedByLender(applicationsGroupedByLender)"
						:key="lenderCode"
						class="ApplicationsBox no-margin-grid"
					>
						<PCALenderContainer
							:lendersThatAllowAutoSubmit="lendersThatAllowAutoSubmit ?? []"
							:lenderName="getLenderInformation(lenderCode)?.lenderName ?? applications?.[0]?.lender?.lenderName"
							:lenderCode="lenderCode"
							:applications="applications"
							:onlyShowBalloonLenders="onlyShowBalloonLenders"
							:lendersList="lendersList"
							:reserveProfitCalcs="reserveProfitCalcs"
							:fimenu="fimenu"
							:send="handleSendButtonAction"
							:dealHasNoLender="dealHasNoLender"
							:showBalloonIcon="showBalloonIcon"
							:handleSelectLender="handleSelectLender"
							:balloonLenders="balloonLenders"
							:openSendModal="openSendModal"
							:fetchDynamicFields="fetchDynamicFields"
							:anyApplicationIsSelected="applicationIsSelected"
							:loadingCustomFields="loadingCustomFields"
							@ApplicationSelected="handleApplicationSelected"
						/>
					</div>
				</div>
			</div>
		</Panel>
	</div>
</template>

<script setup lang="ts">
	import { computed, inject, nextTick, onMounted, Ref, ref, shallowRef, useTemplateRef, watch, watchEffect } from 'vue';
	import CreditApplication, { CreditApplicationDecision, CreditApplicationType } from '@core/classes/CreditApplication';
	import {
		AddProgramCode_ALLY,
		CreditApplicationsEnabled,
		DisplayCreditApplication,
		RetrieveCustomFields,
		SubmitCreditApplication,
	} from '@core/helpers/finance-channels-helper';
	import ENUMS, { DEAL_STATUS, DROPDOWN_SORT_MODE, PARTNER_CODES, PRODUCT_TYPES } from '@core/classes/Enums';
	import { DealType, DealTypeEnum, FinanceChannelsProvider, PartnerCodeType } from '@core/classes/SharedEnums';
	import util, { EventBusCore } from '@core/services/util';
	import $modal from '@core/services/modal';
	import _ from 'underscore';
	import api from '@core/services/api/_api';
	import { SetStatusToReverifyIfNeeded } from '@core/classes/FIMenuHelper';

	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import IsBusyScreenComponent from '@core/components/IsBusyScreenComponent.vue';
	import ModalLenderInformation from '@/modals/modalLenderInformation.vue';
	import modalRouteOneSend from '@/modals/modalRouteOneSend.vue';
	import Panel from '@core/components/Panel.vue';
	import PanelCreditApplicationErrorMessage from './PanelCreditApplicationErrorMessage.vue';
	import PanelDealTerms from './PanelDealTerms.vue';
	import Pill from '@core/components/Pill.vue';

	import { AlternativeLenderCode } from '@core/classes/AlternativeLenderCode';
	import FIDealJacketAdditionalInformation from '@core/classes/FIDealJacketAdditionalInformation';
	import { FIDynamicFields, FIField } from '@core/classes/FIDynamicFields';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuCreditApplication from '@core/classes/FIMenuCreditApplication';
	import FIMenuLender from '@core/classes/FIMenuLender';
	import IReserveProfitCalc from '@core/classes/IReserveProfitCalc';
	import LenderDraft from '@core/classes/LenderDraft';
	import LenderLease from '@core/classes/LenderLease';
	import LenderRetail from '@core/classes/LenderRetail';
	import LenderVersionClass from '@core/classes/LenderVersionClass';
	import PanelBuyersOrder from './PanelBuyersOrder.vue';
	import PartnerCode from '@core/classes/Store/PartnerCode';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import settings from '@/settings';
	import VersionsLender from '@core/classes/LenderVersionClass';
	import StoreClass from '@core/classes/Store/Store';
	import SaturnResponse from '@core/classes/Responses/SaturnResponse';
	import { AxiosResponse } from 'axios';
	import { SubmitCreditApplicationResponse } from '@core/classes/Responses/SubmitCreditApplicationResponse';
	import LogRocket from 'logrocket';
	import PCALenderContainer from '@/components/PCALenderContainer.vue';
	import LenderStatusIcons from '@/components/LenderStatusIcons.vue';

	const emit = defineEmits(['ApplicationSelected']);

	export interface ProviderStoreResponse {
		stores: Record<string, LenderAndAllowedDealTypes[]>;
	}

	export interface LenderAndAllowedDealTypes {
		providerLenderCode: string;
		dealTypes: DealTypeEnum[];
	}

	//#region Common Variables
	const $global = inject<SaturnGlobalPlugin>('$global');
	const balloonLenders = inject<string[]>('balloonLenders', []);
	const balloonIconCondition = inject<any>('balloonIconCondition', null);
	const bo = inject<Ref<InstanceType<typeof PanelBuyersOrder>>>('buyersOrder');
	const onlyShowBalloonLenders = inject<boolean>('onlyShowBalloonLenders', false);
	const activeCreditApplicationOrLender = inject<Ref<boolean>>('activeCreditApplicationOrLender', ref(false));
	const newApplicationSelected = ref(false);

	const props = defineProps<{
		fimenu?: FIMenu | null;
		validation?: any;
	}>();
	//#endregion

	let sortBySelectionStatus = true;

	const dropdownSelectedLender = ref<VersionsLender>(null);
	const lendersList = computed<VersionsLender[]>(() => $global.Lenders ?? []);

	const lendersAvailablePerStore = ref<ProviderStoreResponse>({ stores: {} });

	// List of the provider lender codes and their deal types.
	const lenderCodesWithDealTypes = computed<LenderAndAllowedDealTypes[]>(() => {
		if (!lendersAvailablePerStore.value.stores) return [];
		return Object.keys(lendersAvailablePerStore.value.stores).flatMap(dealerCode => {
			const financeSources = lendersAvailablePerStore.value.stores[dealerCode];
			return financeSources;
		});
	});
	const lendersThatAllowAutoSubmit = computed<string[]>(() => {
		if (!lendersAvailablePerStore.value) return [];
		// Lenders are available if lender allows current deal type
		return lenderCodesWithDealTypes.value
			.filter(x => {
				const dealTypes = x.dealTypes; // Access the dealTypes property directly
				return Array.isArray(dealTypes) && dealTypes.includes(dealTypeEnum.value);
			})
			.map(x => x.providerLenderCode);
	});
	const isLoading = ref(false);
	const isEvaluatingExpresions = ref(false);
	const reserveProfitCalcs = ref<IReserveProfitCalc[]>([]);

	const loadingCustomFields = ref(false);
	const lenderCustomFields = ref<FIDynamicFields>(new FIDynamicFields());

	const lendersThatCanSubmit = ref<string[]>([]);

	const canSubmitCreditApplication = computed(() => {
		if (props.fimenu.isCash()) return false;
		if (!props.fimenu.creditApplicationsEnabled) return false;
		const alternativeLenderCodes = dropdownSelectedLender?.value?.alternativeLenderCodes ?? ([] as AlternativeLenderCode[]);
		return alternativeLenderCodes.some((x: AlternativeLenderCode) => lendersThatAllowAutoSubmit.value.includes(x.code));
	});

	const balloonAmountIsNeeded = computed(() => props.fimenu.isBalloon() && !props.fimenu.loanTerms.atLeastOneBalloonAmount());
	const applicationIsSelected = computed(() => creditApplications.value?.some(app => app.selected));

	const sortedLenderList = computed(() => {
		if (!props.fimenu.creditApplicationsEnabled) return filteredLenderList.value;

		return [...filteredLenderList.value].sort((l1, l2) => {
			// Get R1Codes
			const r1Codes1 = l1.alternativeLenderCodes;
			const r1Codes2 = l2.alternativeLenderCodes;

			// Handle cases where both R1Codes lists are empty
			if (r1Codes1.length === 0 && r1Codes2.length === 0) {
				return l1.lenderName.localeCompare(l2.lenderName);
			}

			// Handle cases where only one R1Codes list is empty
			if (r1Codes1.length === 0) return 1; // Put l1 after l2
			if (r1Codes2.length === 0) return -1; // Put l1 before l2

			// Check if any code in each list matches associated finance sources
			const hasAssociated1 = r1Codes1.some(code => lendersThatAllowAutoSubmit.value.includes(code.code));
			const hasAssociated2 = r1Codes2.some(code => lendersThatAllowAutoSubmit.value.includes(code.code));

			if (hasAssociated1 && !hasAssociated2) return -1; // Place lender with associated codes first
			if (!hasAssociated1 && hasAssociated2) return 1; // Place lender with associated codes second

			// If both or neither are associated, fallback to lenderName sorting
			return l1.lenderName.localeCompare(l2.lenderName);
		});
	});
	const previouslySelectedLender = ref<string | null>(props.fimenu.dealJacket?.getSelectedApplication()?.lender?.lenderCode);

	const getLenderInformation = (lenderCode: string) => {
		var lenderInformation = lendersList.value.find(x => x.lenderCode == lenderCode);

		return lenderInformation;
	};

	const disableViewbuttonForCash = (lender: VersionsLender) => {
		return lender.lenderName.toLowerCase() == 'cash';
	};

	const handleApplicationSelected = (application: FIMenuCreditApplication) => {
		sortBySelectionStatus = false;
	};

	const filteredLenderList = computed<VersionsLender[]>(() => {
		return lendersList.value.filter(x => {
			const lenderByDealType = x.getLenderByDealType(dealType.value);
			if (x.lenderCode == 'UNLISTED') return false;
			if (lenderByDealType.divisions.length <= 0) return false;

			if (props.fimenu.isCash()) return true;
			if (!props.fimenu.creditApplicationsEnabled) return true;

			if (onlyShowBalloonLenders) {
				return balloonLenders.includes(x.lenderCode);
			}

			// Add the lenders that have R1 code setup for this store, regardless of the divisions.
			if (x.alternativeLenderCodes.some((code: AlternativeLenderCode) => lendersThatAllowAutoSubmit.value.includes(code.code))) return true;

			// Filter lenders without division.
			// We should use getDefaultDivision when we use proper division types.
			if (!x.displayLender) return false;

			return true;
		});
	});

	const creditApplications = computed<FIMenuCreditApplication[]>(() => {
		// const returnvalue = props.fimenu.dealJacket.applications.filter((ca: FIMenuCreditApplication) => ca.MatchesDealType(props.fimenu.dealType));
		const returnvalue = props.fimenu.dealJacket.applications;
		return _.sortBy(returnvalue, (r: FIMenuCreditApplication) => r.dateCreated).reverse();
	});

	const applicationsGroupedByLender = computed(() => {
		return creditApplications.value.reduce(
			(acc, app) => {
				const lenderCode = app.lender.lenderCode;
				if (!acc[lenderCode]) {
					acc[lenderCode] = [];
				}
				acc[lenderCode].push(app);
				return acc;
			},
			{} as Record<string, FIMenuCreditApplication[]>,
		);
	});

	// We only sort the applications if the sortBySelectionStatus is true.
	// Which is before we select any applications.
	const getSortedApplicationsGroupedByLender = (applicationsGroupedByLender: Record<string, FIMenuCreditApplication[]>) => {
		return Object.keys(applicationsGroupedByLender)
			.sort((a, b) => {
				if (a == previouslySelectedLender.value) return -1;
				if (b == previouslySelectedLender.value) return 1;

				if (!sortBySelectionStatus) return a.localeCompare(b);
				// Default sorting - by selection status then alphabetically
				// Check if any application in group 'a' is selected
				const aHasSelected = applicationsGroupedByLender[a].some(app => app.selected);
				// Check if any application in group 'b' is selected
				const bHasSelected = applicationsGroupedByLender[b].some(app => app.selected);

				// If 'a' has selected but 'b' doesn't, 'a' comes first
				if (aHasSelected && !bHasSelected) return -1;
				// If 'b' has selected but 'a' doesn't, 'b' comes first
				if (!aHasSelected && bHasSelected) return 1;
				// Otherwise, sort alphabetically by lender code
				return a.localeCompare(b);
			})
			.reduce(
				(obj, key) => {
					obj[key] = applicationsGroupedByLender[key];
					return obj;
				},
				{} as Record<string, FIMenuCreditApplication[]>,
			);
	};

	const creditApplicationsWithBalloon = computed(() => creditApplications.value.filter(app => app.submissionInformation.balloonAmount > 0));

	const dealType = computed<DealType>(() => {
		return props.fimenu.isFinance() ? DealType.Retail : props.fimenu.isLease() ? DealType.Lease : DealType.Draft;
	});

	const dealTypeEnum = computed<DealTypeEnum>(() => {
		return props.fimenu.isFinance() ? DealTypeEnum.Finance : props.fimenu.isLease() ? DealTypeEnum.Lease : DealTypeEnum.Cash;
	});

	const dealLenderInformation = computed<VersionsLender | null>(() => filteredLenderList.value.find(x => x.lenderCode == props.fimenu.lender.lenderCode));

	const currentPreselectedLender = computed(() =>
		preSelectedLenders.value?.find(lender => lender.lenderCode == props.fimenu.lender?.lenderCode && props.fimenu.lender.isActiveOnDeal),
	);

	// Preselected lenders that don't have credit applications.
	const preSelectedLendersWithNoApplications = computed(() =>
		preSelectedLenders.value.filter(lender => !Object.keys(applicationsGroupedByLender.value).includes(lender.lenderCode)),
	);
	const dealHasNoLender = computed(() => !((applicationIsSelected.value || currentPreselectedLender.value) && !dropdownSelectedLender.value));
	const currentLenderAllowsBalloon = computed(
		() => balloonLenders.includes(props.fimenu.lender?.lenderCode) || balloonLenders.includes(dropdownSelectedLender.value?.lenderCode),
	);

	onMounted(async () => {
		props.fimenu.loadLatestestCreditDecision();
		getReserveProfitCalcs();

		if (props.fimenu.isBalloon() && dealHasNoLender.value && !props.fimenu.isDealLocked()) {
			props.fimenu.loanTerms.isBalloon = false;
		}
	});

	const openSendModal = async (lender: VersionsLender | null = null, forcePassthrough: boolean = false) => {
		if (await props.fimenu.checkForFraud()) return;

		$modal.open(modalRouteOneSend, {
			name: 'modalRouteOneSend',
			passedData: {
				fimenu: props.fimenu,
				lender: lender ?? dropdownSelectedLender.value,
				loading: loadingCustomFields.value,
				customFields: lenderCustomFields.value,
				forceAllowPassthrough: forcePassthrough,
			},
			backdrop: false,
			postFunction: (additionalInformation: FIDealJacketAdditionalInformation) => send(lender ?? dropdownSelectedLender.value, additionalInformation),
		});
	};

	const handleAddLender = (lenderCode: string) => {
		props.fimenu.preSelectedLenders = [lenderCode];

		if (props.fimenu.isCash()) {
			const selectedLender = lendersList.value?.find(x => x.lenderCode == lenderCode);
			handleSelectLender(selectedLender);
		} else {
			props.fimenu.lender = new FIMenuLender();
		}
	};

	/**
	 * Assigns a lender to the deal that is not credit application based.
	 */
	const handleSelectLender = async (lender: LenderVersionClass, isApplication = false, selectedProvider: FinanceChannelsProvider = FinanceChannelsProvider.None) => {
		sortBySelectionStatus = false;
		const selectedLenderInformationForTheDeal = lender?.versions[0]?.getLenderByDealType(props.fimenu.dealType as 'Lease' | 'Finance' | 'Cash');

		// Clear credit applications selection.
		clearSelectedLenderApplications(isApplication);
		const selectedLenderIsDifferent = lender.lenderCode !== props.fimenu.lender.lenderCode;
		var fimenuLender = new FIMenuLender({
			divisions: selectedLenderInformationForTheDeal?.divisions ?? [],
			lenderCode: lender.lenderCode,
			lenderContacts: selectedLenderInformationForTheDeal?.lenderContacts ?? [],
			lenderName: lender.lenderName,
			selectedProvider, // When we get new providers; this needs to update to the provider.
			isActiveOnDeal: true,
		});

		props.fimenu.lender = fimenuLender;
		props.fimenu.dealJacket.purchaseVehicleBookValue = null;

		updateLenderOnFIMenu(lender); // Update FIMenu as it goes without credit applications
		setAllPaperworkToMustRepopulate(); // Since the lender changed. IF we have paperwork; they must repopulate.
		setMaxDaysToFirstPayment(lender); // Set the days for max payment.
		if (!props.fimenu.isDealLocked()) {
			props.fimenu.setLenderFees(lender.getLenderByDealType(props.fimenu.dealType));
		}

		props.fimenu.recalculateBuyersOrder();

		if (saveDealIfNeeded(selectedLenderIsDifferent)) props.fimenu.save();
	};

	/* Returns true if the dynamic fields were fetched successfully */
	const fetchDynamicFields = async (lenderCode: string) => {
		try {
			loadingCustomFields.value = true;
			const tokenId = 'get-getCustomFields';
			api.cancel(tokenId);

			const customFields = await RetrieveCustomFields(props.fimenu, lenderCode, 'get-getCustomFields');
			lenderCustomFields.value = customFields;
			return true;
		} catch (err) {
			console.error(err);
			return false;
		} finally {
			loadingCustomFields.value = false;
			// Reset cancel token
		}
	};

	/**
	 * Modifies the lender on the deal object when using the lender drop down
	 * This is the not apply a lender to the deal.
	 */
	const handleLenderChange = async (lender: VersionsLender) => {
		const lenderInfo = lender.getLenderByDealType(props.fimenu.dealType); // either lenderRetail, lenderLease or lenderdraft.
		clearSelectedLenderApplications();
		setMaxDaysToFirstPayment(lender); // Set the days for max payment.
		updateLenderOnFIMenu(lender);

		const lenderIsAvailableToSubmit =
			props.fimenu.creditApplicationsEnabled && lender.alternativeLenderCodes.some((x: AlternativeLenderCode) => lendersThatAllowAutoSubmit.value.includes(x.code));
		// If we select a lender that is available for submission
		if (lenderIsAvailableToSubmit && props.fimenu.routeOneEnabled()) {
			// let providerLendercode = await handleFetchPartnerId(lenderInfo, lender.lenderCode); // Get the lender code as response.
			fetchDynamicFields(lender.lenderCode);
		}

		SetStatusToReverifyIfNeeded(props.fimenu);

		if (!props.fimenu.isDealLocked()) {
			props.fimenu.setLenderFees(lender.getLenderByDealType(props.fimenu.dealType));
		}

		props.fimenu.recalculateBuyersOrder();
	};

	const handleFetchPartnerId = async (lender: LenderRetail | LenderLease | LenderDraft, lenderCode: string): Promise<string | null> => {
		try {
			isEvaluatingExpresions.value = true;
			if (!lenderCode) return null;

			const response = await api.lenders.getLenderPartnerId(props.fimenu, lenderCode);

			return response.data.payload;
		} catch (ex) {
			console.error(ex);
		} finally {
			isEvaluatingExpresions.value = false;
		}
	};
	const updateLenderOnFIMenu = (lender: VersionsLender) => {
		// Copy from PanelLEnder.
		const lenderVersion = lender.getLenderByDealType(dealType.value); // Get the version of the lender. Either Retail, Lease or Cash.
		const selectedLenderIsDifferent = lender.lenderCode !== props.fimenu.lender.lenderCode; // wether the lender is the same.

		if (selectedLenderIsDifferent) {
			// Create FImenuLender and assign it to the deal.
			props.fimenu.lender = new FIMenuLender({
				divisions: lenderVersion.divisions,
				lenderContacts: lenderVersion.lenderContacts,
				lenderCode: lender.lenderCode,
				lenderName: lender.lenderName,
				selectedProvider: lender.alternativeLenderCodes.length > 0 ? FinanceChannelsProvider.RouteOne : FinanceChannelsProvider.None,
			});
		}
	};

	const handleSendButtonAction = () => {
		if (canSubmitCreditApplication.value && !loadingCustomFields.value) {
			// If deal is locked. We should do nothing.
			if (props.fimenu.isDealLocked()) return util.toastr('error', 'Error', 'Cannot send credit applications. Deal is locked.');
			openSendModal();
		} else {
			clearDropdownLender();
			handleAddLender(dropdownSelectedLender.value.lenderCode);
		}
	};
	const saveDealIfNeeded = (selectedLenderIsDifferent: boolean): boolean => {
		const isLenderInvalid = util.isNull(props.validation) ? false : props.validation.lender.lenderCode.$invalid;
		if (!isLenderInvalid && props.fimenu.isDealLocked() && !props.fimenu.isSpectator && selectedLenderIsDifferent) {
			return true;
		}

		return false;
	};

	const setMaxDaysToFirstPayment = (lender: VersionsLender) => {
		const lenderVersion = lender.getLenderByDealType(dealType.value);

		if (props.fimenu.loanTerms.maxDaysToFirstPayment != undefined && props.fimenu.loanTerms.maxDaysToFirstPayment != null) return;

		if (lenderVersion && 'maxDaysToFirstPayment' in lenderVersion && lenderVersion.maxDaysToFirstPayment) {
			props.fimenu.loanTerms.maxDaysToFirstPayment = lenderVersion.maxDaysToFirstPayment;
		} else {
			props.fimenu.loanTerms.maxDaysToFirstPayment = 45;
		}
	};

	const setAllPaperworkToMustRepopulate = () => {
		//set all current packet document to must repopulate
		if (props.fimenu.hasPaperworkPacket()) {
			props.fimenu.paperwork.currentPacket().documents.forEach((d: any) => {
				d.mustRepopulate = true;
			});
		}
	};

	const getLatestCreditAppFieldsResponse = async () => {
		try {
			const currentStore: StoreClass | null = settings.userStores.find((s: StoreClass) => s.storeCode == props.fimenu.store.storeCode);
			if (currentStore == null) return;

			const storeCodeList = currentStore.partnerCodes?.filter(pc => pc.type == PartnerCodeType.RouteOneDealerId).map(x => x.code) ?? [];
			if (storeCodeList.length <= 0) return;

			const response: AxiosResponse<SaturnResponse<ProviderStoreResponse>> = await api.financeChannels.getAvailableLenderCodesForSubmission(storeCodeList);
			LogRocket.log('Lenders by store', response.data);
			lendersAvailablePerStore.value = response.data.payload;
		} catch (err) {
			console.error(err);
		}
	};

	/**
	 * Modifies the credit application request based on the lender requirements
	 *
	 * @param lender - The lender to which the credit application is being submitted
	 * @param request - The additional information object for the credit application
	 */
	const modifyCreditApplicationRequest = (lender: VersionsLender, request: FIDealJacketAdditionalInformation) => {
		try {
			const allyOperation = () => {
				// Handle Ally-specific requirements
				const fields = request.dynamicFields.fields;
				// Find the Program Code field in the dynamic fields
				const programCodeField = fields.find(field => field.name == 'Special Programs');

				// If we have a program code that is not SPR. Lets add it to the comments
				if (programCodeField?.value != null && programCodeField.value != 'SPR Program') {
					request.comment ??= '';
					request.comment += `\n\n  Program Code: ${programCodeField.value}`;
				}
				// If Program Code exists but is not set to "SPR Program", update it
				if (programCodeField != null) {
					programCodeField.value = 'SPR Program';
				} else {
					// If Program Code field doesn't exist, add it using the helper function
					AddProgramCode_ALLY(request);
				}
			};

			switch (lender.lenderCode) {
				case 'ALLY':
					allyOperation();
					break;
				default:
					break;
			}
		} catch (err) {
			console.error(err);
		}
	};

	const send = async (lender: VersionsLender, additionalInformation: FIDealJacketAdditionalInformation) => {
		// You could still see this component if you have previous credit applications. But cannot send new one if we
		// dont have credentials or is disabled.
		if (!CreditApplicationsEnabled(props.fimenu)) return util.toastr('error', 'Error', 'Cannot send credit applications. Currently disabled.');

		if (props.fimenu.isSpectator) return false;

		// Performs modification/overrides to the request based on not normal rules.
		//modifyCreditApplicationRequest(lender, additionalInformation);

		isLoading.value = true;
		try {
			const response = await SubmitCreditApplication(props.fimenu, additionalInformation, lender);

			const concatMessage = response?.messages?.join(', ') ?? null;

			if (response?.statusCode < 400) {
				const parsedResponse = new SubmitCreditApplicationResponse(response.payload);

				props.fimenu.dealJacket.addApplication(parsedResponse.application);

				util.toastr('success', 'Credit Application', concatMessage?.trim() ? concatMessage : 'Submitted successfully.');
				dropdownSelectedLender.value = null;
			} else {
				util.toastr('error', 'Credit Application', concatMessage?.trim() ? concatMessage : 'There was an error submitting your credit application');
				console.error(JSON.stringify(response));
			}
		} catch (err) {
			console.error(err);
		} finally {
			isLoading.value = false;
		}
	};

	const valuePredicate = (l: VersionsLender) => {
		return l;
	};

	const comparerPredicate = (a: VersionsLender, b: VersionsLender) => {
		return a && b && a.lenderCode === b.lenderCode;
	};

	const displayPredicate = (l: VersionsLender) => {
		return l.lenderName;
	};

	const descriptionPredicate = (l: VersionsLender) => {
		if (
			l.alternativeLenderCodes.length > 0 &&
			l.alternativeLenderCodes.some((x: AlternativeLenderCode) => lendersThatAllowAutoSubmit.value.includes(x.code)) &&
			props.fimenu.creditApplicationsEnabled
		) {
			return 'R1';
		}

		return null;
	};

	async function getReserveProfitCalcs() {
		const { data } = await api.settings.getGetReserveProfitCalcs();
		if (data && Array.isArray(data)) reserveProfitCalcs.value = data;
		else console.error("Couldn't get reserve profit calcs. Wrong Data?");
	}

	getLatestCreditAppFieldsResponse();

	const preSelectedLenders = computed<VersionsLender[]>(() => {
		return props.fimenu.preSelectedLenders.map((lenderCode: string) => getLenderInformation(lenderCode));
	});

	function clearSelectedLenderApplications(isApplication = false) {
		if (!isApplication) creditApplications.value.forEach(x => (x.selected = false));
		props.fimenu.lender.isActiveOnDeal = false;
	}

	// #region DealTerm Logic
	//RECALCULATE BUYERS ORDER WHEN DEAL TERM CHANGE
	function dealTermsChangedHandler() {
		if (props.fimenu.buyersOrderEnabled) {
			bo.value.recalculate();
		}
	}

	function clearDropdownLender() {
		nextTick(() => {
			dropdownSelectedLender.value = null;
		});
	}

	const displayBOPanelDealTerms = computed(() => {
		const currentBO = props.fimenu.getCurrentBuyersOrder();

		return (
			props.fimenu.dealType != null &&
			props.fimenu.hasCoverageTerms &&
			(DisplayCreditApplication(props.fimenu) || props.fimenu.lender.lenderCode) &&
			props.fimenu.buyersOrderEnabled &&
			currentBO &&
			currentBO.isValid() &&
			currentBO.areAllBucketsValid()
		);
	});

	const dealTermsPanelObj = computed(() => {
		let title;
		if (props.fimenu.isDraft()) title = 'Draft Terms';
		else if (props.fimenu.isCash()) title = 'Cash Terms';
		else if (props.fimenu.isFinance()) title = 'Loan Terms';
		else title = 'Lease Terms';

		return { title, action: [] as any[] };
	});

	const applicationLenderPanel = useTemplateRef('applicationLenderPanel');
	const inputsend = useTemplateRef('inputsend');

	const showBalloonIcon = (lenderCode: string) => {
		if (!props.fimenu.isFinance()) return false;
		return props.fimenu.hasPaperworkPacket() ? balloonLenders.includes(lenderCode) && props.fimenu.loanTerms.isBalloon : balloonLenders.includes(lenderCode);
	};

	//#endregion

	const approvedApplications = computed(() => {
		return creditApplications.value.filter(x => x.status == CreditApplicationDecision.Approved).length;
	});
	const conditionalApplications = computed(() => {
		return creditApplications.value.filter(x => x.status == CreditApplicationDecision.Condition).length;
	});
	const deniedApplications = computed(() => {
		return creditApplications.value.filter(x => x.status == CreditApplicationDecision.Denied).length;
	});

	// This provides feedback for the specctator when the editor changes lender.
	watch(
		() => props.fimenu.lender.lenderCode,
		(newVal, oldVal) => {
			if (props.fimenu.isSpectator) {
				dropdownSelectedLender.value = lendersList.value.find(x => x.lenderCode == props.fimenu.lender.lenderCode);
			}
		},
	);

	watch(
		dealHasNoLender,
		() => {
			activeCreditApplicationOrLender.value = !dealHasNoLender.value;
		},
		{ immediate: true },
	);

	// Handle balloon mode for deal when switching lenders
	watchEffect(() => {
		if (!props.fimenu.isFinance() || props.fimenu.hasPaperworkPacket()) return;

		const preventBalloonReset = currentLenderAllowsBalloon.value && !props.fimenu.isSpectator;
		if (!preventBalloonReset) {
			// props.fimenu.loanTerms.resetBalloonAmounts();
			props.fimenu.loanTerms.isBalloon = false;
		}

		props.fimenu.loanTerms.isBalloon = currentLenderAllowsBalloon.value;
	});

	watch(
		() => [currentPreselectedLender.value, applicationIsSelected.value],
		([newLender, newApp]) => {
			if (newLender || newApp) clearDropdownLender();
		},
	);

	watch(
		() => props.fimenu.dealType,
		() => {
			clearDropdownLender();
			preSelectedLenders.value.splice(0);
		},
	);
</script>
<style>
	.panel-routeOneDealerTrack .approved-lender-icon {
		color: var(--success-color);
	}

	.panel-routeOneDealerTrack .conditional-lender-icon {
		color: var(--print-color);
	}

	.panel-routeOneDealerTrack .denied-lender-icon {
		color: var(--error-color);
	}

	.panel-routeOneDealerTrack {
		height: calc(100% - 271px);
	}

	.panel-routeOneDealerTrack .AccordionSection .children-container {
		min-height: unset !important;
	}

	.panel-routeOneDealerTrack .action-column1 {
		width: 28px;
	}

	.panel-routeOneDealerTrack .open-credit-app {
		width: 43px;
	}

	.credit-application-lender-wrapper {
		align-items: center;
	}

	.panel-routeOneDealerTrack .rich-table-rows .action-cell button {
		width: 40px;
		height: 40px;
		justify-content: space-between;
		margin: 0;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.panel-routeOneDealerTrack .terms-grid {
		grid-template-columns: 40px 140px 90px 80px;
	}

	.lender-selector {
		font-size: 25px;
	}

	.input-checkbox-container {
		display: block;
		max-width: 28px;
	}

	.applications-box-container {
		display: flex;
		flex-direction: column;
		gap: 5px;
	}

	.ApplicationsBox .input-checkbox {
		padding: unset;
	}

	.SubmissionIcon {
		color: var(--main-color);
		font-size: 13px;
	}

	.footer-container {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	}

	.tier-text {
		display: inline-block;
		max-width: 50px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
</style>
